import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { ChevronRightIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline'
import { cn } from "../lib/utils"

function Breadcrumb({ className, ...props }: React.ComponentProps<"nav">) {
    return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} className={cn("min-h-[36px] flex items-center justify-center", className)} />
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
    return (
        <ol
            data-slot="breadcrumb-list"
            className={cn(
                "text-muted-foreground flex flex-wrap items-center gap-1.5 text-md break-words sm:gap-2.5",
                className
            )}
            {...props}
        />
    )
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
    return (
        <li data-slot="breadcrumb-item"
            className={cn("inline-flex items-center gap-1.5", className)}
        >
            <span {...props}></span>
            <BreadcrumbSeparator />
        </li>
    )
}

function BreadcrumbLastItem({ className, ...props }: React.ComponentProps<"li">) {
    return (
        <li data-slot="breadcrumb-item"
            className={cn("inline-flex items-center gap-1.5", className)}
            {...props}
        />
    )
}

function BreadcrumbLink({
    asChild,
    className,
    ...props
}: React.ComponentProps<"a"> & {
    asChild?: boolean
}) {
    const Comp = asChild ? Slot : "a"

    return (
        <Comp
            data-slot="breadcrumb-link"
            className={cn("hover:text-foreground text-md transition-colors", className)}
            {...props}
        />
    )
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="breadcrumb-page"
            role="link"
            aria-disabled="true"
            aria-current="page"
            className={cn("text-foreground text-md font-normal cursor-default", className)}
            {...props}
        />
    )
}

function BreadcrumbSeparator({
    children,
    className,
    ...props
}: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="breadcrumb-separator"
            role="presentation"
            aria-hidden="true"
            className={cn("[&>svg]:size-3.5", className)}
            {...props}
        >
            {children ?? <ChevronRightIcon height={13} />}
        </span>
    )
}

function BreadcrumbEllipsis({
    className,
    ...props
}: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="breadcrumb-ellipsis"
            role="presentation"
            aria-hidden="true"
            className={cn("flex size-9 items-center justify-center", className)}
            {...props}
        >
            <EllipsisHorizontalIcon className="size-4" />
            <span className="sr-only">More</span>
        </span>
    )
}

export {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLastItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbEllipsis,
}
